import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSeriesOccurrences, cancelOccurrence, cancelSeries } from '../../services/bookingService';
import { formatPattern } from '../../services/recurringService';
import toast from 'react-hot-toast';
import BackButtonBottom from '../../components/common/BackButtonBottom';

export default function SeriesDetail() {
  const { seriesId } = useParams();
  const navigate = useNavigate();
  const [series, setSeries] = useState(null);
  const [occurrences, setOccurrences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingSeries, setCancellingSeries] = useState(false);
  const [cancellingOccurrence, setCancellingOccurrence] = useState(null);
  const [showCancelAllModal, setShowCancelAllModal] = useState(false);
  const [cancelScope, setCancelScope] = useState('free');

  const reload = () => {
    setLoading(true);
    getSeriesOccurrences(seriesId)
      .then((res) => {
        setSeries(res.series);
        setOccurrences(res.occurrences || []);
      })
      .catch((err) => toast.error(err.message || 'Could not load series'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, [seriesId]);

  const handleCancelOccurrence = async (bookingId) => {
    if (!window.confirm('Cancel this occurrence? Standard cancellation fees may apply.')) return;
    setCancellingOccurrence(bookingId);
    try {
      const result = await cancelOccurrence(bookingId, 'rider_cancelled_occurrence');
      toast.success(result.cancellationFee > 0
        ? `Cancelled. $${result.cancellationFee.toFixed(2)} fee applied.`
        : 'Cancelled — no fee.');
      reload();
    } catch (err) {
      toast.error(err.message || 'Cancellation failed');
    }
    setCancellingOccurrence(null);
  };

  const handleCancelSeries = async () => {
    setCancellingSeries(true);
    try {
      const result = await cancelSeries(seriesId, 'rider_cancelled_series', cancelScope);
      toast.success(`${result.cancelledCount} occurrences cancelled. Total fees: $${result.totalFees.toFixed(2)}`);
      setShowCancelAllModal(false);
      reload();
    } catch (err) {
      toast.error(err.message || 'Series cancellation failed');
    }
    setCancellingSeries(false);
  };

  if (loading) return <div className="p-10 text-center text-muted">Loading...</div>;
  if (!series) return <div className="p-10 text-center text-muted">Series not found</div>;

  const futureOccurrences = occurrences.filter((o) =>
    ['pending_fare_calc', 'pending_pool', 'pending_targeted', 'pending', 'confirmed', 'en_route', 'arrived', 'in_progress'].includes(o.status)
  );
  const pastOccurrences = occurrences.filter((o) => ['completed', 'cancelled'].includes(o.status));

  const occBadge = (o) => {
    if (o.status === 'completed') return { bg: 'rgba(45,212,168,0.1)', color: '#059669', label: '✓ Done' };
    if (o.status === 'cancelled') return { bg: '#fee2e2', color: '#991b1b', label: 'Cancelled' };
    if (o.status === 'pending_fare_calc') return { bg: '#fef3c7', color: '#92400e', label: 'Pricing…' };
    if (o.status === 'confirmed') return { bg: 'rgba(99,102,241,0.1)', color: '#4f46e5', label: 'Confirmed' };
    if (o.status === 'in_progress') return { bg: '#dbeafe', color: '#1e40af', label: 'In progress' };
    return { bg: '#f1f5f9', color: '#5a6578', label: o.status.replace(/_/g, ' ') };
  };

  return (
    <div className="max-w-[700px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate('/rider/series')} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← All series</button>
      </div>

      <h1 className="font-serif mt-0 mb-1">
        {series.referenceNumber}
      </h1>
      <div className="text-[0.9rem] text-body mb-4">
        {formatPattern(series.pattern)}
      </div>

      <div className="p-4 rounded-2xl border border-slate-200 mb-4 bg-white">
        <div className="text-[0.85rem] text-body">
          <div><strong>Pickup:</strong> {series.pickupAddress}</div>
          <div><strong>Destination:</strong> {series.destinationAddress}</div>
          <div className="mt-2">
            <strong>Total:</strong> {series.occurrenceCount} occurrences
            {series.fareStatus === 'complete' && <> · ~${series.totalEstimatedFare?.toFixed(2)} estimated</>}
            {series.fareStatus === 'calculating' && <> · Pricing ({series.occurrencesPriced || 0}/{series.occurrenceCount})</>}
          </div>
        </div>
      </div>

      {/* Cancel all */}
      {series.status === 'active' && futureOccurrences.length > 0 && (
        <button
          onClick={() => setShowCancelAllModal(true)}
          className="w-full p-3 rounded-xl border-[1.5px] bg-white cursor-pointer font-semibold mb-4"
          style={{ borderColor: '#ff6b6b', color: '#ff6b6b' }}
        >Cancel remaining series</button>
      )}

      {showCancelAllModal && (
        <div className="p-[18px] rounded-2xl bg-white mb-4" style={{ border: '1px solid #fecaca' }}>
          <h3 className="mt-0 mb-3 font-serif" style={{ color: '#dc2626' }}>Cancel remaining occurrences?</h3>
          <div className="form-group">
            <label className="form-label">Scope</label>
            <select className="form-input form-select" value={cancelScope} onChange={(e) => setCancelScope(e.target.value)}>
              <option value="free">Cancel only future occurrences ≥24hr away (no fees)</option>
              <option value="all">Cancel ALL future occurrences (standard fees apply per occurrence)</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowCancelAllModal(false)} className="flex-1 p-3 rounded-[10px] border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold">Keep series</button>
            <button onClick={handleCancelSeries} disabled={cancellingSeries} className="flex-1 p-3 rounded-[10px] border-0 text-white cursor-pointer font-bold"
              style={{ background: '#ff6b6b' }}>{cancellingSeries ? '...' : 'Confirm cancel'}</button>
          </div>
        </div>
      )}

      <h3 className="mt-6 mb-3 text-brand-dark">Upcoming ({futureOccurrences.length})</h3>
      <div className="grid gap-2">
        {futureOccurrences.map((o) => {
          const b = occBadge(o);
          const pickupTime = o.scheduledPickupTime?._seconds
            ? new Date(o.scheduledPickupTime._seconds * 1000)
            : new Date(o.scheduledPickupTime);
          return (
            <div key={o.id} className="p-3.5 rounded-xl bg-white border border-slate-200">
              <div className="flex justify-between items-start mb-1.5">
                <div>
                  <div className="font-semibold text-brand-dark">
                    {pickupTime.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </div>
                  <div className="text-[0.8rem] text-body">
                    {pickupTime.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
                    {o.fareBreakdown && <> · ${o.fareBreakdown.totalFare.toFixed(2)}</>}
                  </div>
                </div>
                <span className="px-2 py-0.5 rounded-full text-[0.7rem] font-bold" style={{ background: b.bg, color: b.color }}>
                  {b.label}
                </span>
              </div>
              <div className="flex gap-1.5 mt-2">
                {['confirmed', 'en_route', 'arrived', 'in_progress'].includes(o.status) && (
                  <button onClick={() => navigate(`/rider/trip/${o.id}`)} className="flex-1 p-2 rounded-lg border-[1.5px] bg-white cursor-pointer font-semibold text-[0.8rem]"
                    style={{ borderColor: '#2dd4a8', color: '#2dd4a8' }}>View trip</button>
                )}
                <button
                  onClick={() => handleCancelOccurrence(o.id)}
                  disabled={cancellingOccurrence === o.id}
                  className="flex-1 p-2 rounded-lg border-[1.5px] border-slate-200 bg-white cursor-pointer font-semibold text-[0.8rem]"
                  style={{ color: '#ff6b6b' }}
                >{cancellingOccurrence === o.id ? '...' : 'Cancel this one'}</button>
              </div>
            </div>
          );
        })}
        {futureOccurrences.length === 0 && (
          <div className="p-4 text-center text-muted text-[0.85rem]">No upcoming occurrences</div>
        )}
      </div>

      {pastOccurrences.length > 0 && (
        <>
          <h3 className="mt-6 mb-3 text-brand-dark">Past ({pastOccurrences.length})</h3>
          <div className="grid gap-1.5">
            {pastOccurrences.map((o) => {
              const b = occBadge(o);
              const pickupTime = o.scheduledPickupTime?._seconds
                ? new Date(o.scheduledPickupTime._seconds * 1000)
                : new Date(o.scheduledPickupTime);
              return (
                <div key={o.id} className="p-2.5 rounded-[10px] bg-white border border-slate-200 flex justify-between items-center">
                  <div className="text-[0.85rem]">
                    {pickupTime.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                    {' · '}{pickupTime.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
                    {o.fareBreakdown && <span className="text-muted"> · ${o.fareBreakdown.totalFare.toFixed(2)}</span>}
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[0.68rem] font-bold" style={{ background: b.bg, color: b.color }}>
                    {b.label}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    <BackButtonBottom to="/rider/series" label="All series" />
    </div>
  );
}